import React, { useEffect, useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeBlock = ({ json }) => (
  <pre className="text-xs bg-muted/40 p-3 rounded border border-border overflow-auto max-h-72">
    {JSON.stringify(json, null, 2)}
  </pre>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between py-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-foreground font-mono">{value}</span>
  </div>
);

const to2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

const DebugModal = ({ isOpen, onClose, data }) => {
  const [tab, setTab] = useState('summary');

  useEffect(() => {
    if (isOpen) setTab('summary');
  }, [isOpen]);

  if (!isOpen || !data) return null;

  const raw = data?.raw_extracted_data?.data || {};
  const lines = raw?.line_items || [];

  const rollup = useMemo(() => {
    const net = lines.reduce((s, it) => s + (parseFloat(it?.net_amount) || (parseFloat(it?.quantity||0)*parseFloat(it?.unit_price||0)) || 0), 0);
    const vat = lines.reduce((s, it) => s + (parseFloat(it?.vat_amount) || ((parseFloat(it?.net_amount||0) || (parseFloat(it?.quantity||0)*parseFloat(it?.unit_price||0))) * ((parseFloat(it?.vat_rate||0)||0)/100))), 0);
    const gross = net + vat;
    return { net, vat, gross };
  }, [lines]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Bug" size={20} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">AI Extraction Debug</h3>
            <span className="text-xs text-muted-foreground">Invoice #{data?.invoice_number}</span>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} className="h-8 w-8 p-0" />
        </div>

        <div className="flex border-b border-border">
          {['summary','lines','raw'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm border-b-2 ${tab===t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t==='summary'?'Summary': t==='lines'?'Line Items':'Raw JSON'}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
          {tab === 'summary' && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <Row label="Vendor" value={data?.vendor_name || '—'} />
                <Row label="Invoice #" value={data?.invoice_number || '—'} />
                <Row label="Invoice Date" value={data?.invoice_date || '—'} />
                <Row label="Currency" value={data?.currency || '—'} />
                <Row label="Confidence" value={`${Math.round((parseFloat(data?.confidence||0))*100)}%`} />
              </div>
              <div className="space-y-1">
                <Row label="AI Net (sum of lines)" value={`£${to2(rollup.net)}`} />
                <Row label="AI VAT (sum of lines)" value={`£${to2(rollup.vat)}`} />
                <Row label="AI Gross (sum of lines)" value={`£${to2(rollup.gross)}`} />
                <Row label="Recorded Net" value={`£${to2(parseFloat(data?.net_amount||0))}`} />
                <Row label="Recorded VAT" value={`£${to2(parseFloat(data?.vat_amount||0))}`} />
                <Row label="Recorded Gross" value={`£${to2(parseFloat(data?.gross_amount||0))}`} />
              </div>
            </div>
          )}

          {tab === 'lines' && (
            <div className="space-y-3">
              {lines.length === 0 && (
                <div className="text-sm text-muted-foreground">No line items found in raw_extracted_data.</div>
              )}
              {lines.map((it, i) => {
                const qty = parseFloat(it?.quantity||0)||0;
                const unit = parseFloat(it?.unit_price||0)||0;
                const net = Number.isFinite(it?.net_amount) ? it?.net_amount : qty*unit;
                const rate = Number.isFinite(it?.vat_rate) ? it?.vat_rate : parseFloat(it?.vat_rate||0)||0;
                const vat = Number.isFinite(it?.vat_amount) ? it?.vat_amount : net*rate/100;
                return (
                  <div key={i} className="p-3 border border-border rounded">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-foreground">{it?.description || `Line ${i+1}`}</div>
                      <span className="text-xs text-muted-foreground">VAT: {it?.vat_code || (rate===20?'S':rate===5?'R':rate===0?'Z':'O')} {rate}%</span>
                    </div>
                    <div className="grid grid-cols-5 gap-3 mt-2 text-sm">
                      <Row label="Qty" value={qty} />
                      <Row label="Unit" value={`£${to2(unit)}`} />
                      <Row label="Net" value={`£${to2(net)}`} />
                      <Row label="VAT" value={`£${to2(vat)}`} />
                      <Row label="Total" value={`£${to2(net+vat)}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'raw' && (
            <CodeBlock json={raw} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugModal;

